import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { visitAPI, clientAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime, getStatusColor, toISTInput, istInputToISO } from '../lib/utils';
import {
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Users,
  Clock,
  FileText,
  MoreHorizontal,
  AlertTriangle,
  ClipboardCheck,
  Download,
  Mail,
  MailCheck,
  Edit2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const VisitsPage = () => {
  const [visits, setVisits] = useState([]);
  const [showAllVisits, setShowAllVisits] = useState(false);
  usePageTitle('Visits');
  const { user, isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [staffOnLeave, setStaffOnLeave] = useState({});
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [isSummaryActionOpen, setIsSummaryActionOpen] = useState(false);
  const [summaryRemarks, setSummaryRemarks] = useState('');
  const [summaryEditText, setSummaryEditText] = useState('');
  const [isAdminEditOpen, setIsAdminEditOpen] = useState(false);
  const [adminEditForm, setAdminEditForm] = useState({ report: '', findings: '', completion_summary: '' });

  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    visit_date: '',
    visit_end_time: '17:00',
    assigned_staff: [],
    purpose: '',
  });
  const [isDateRange, setIsDateRange] = useState(false);
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [conflictDialog, setConflictDialog] = useState({ open: false, conflicts: [] });
  const [isEditScheduleOpen, setIsEditScheduleOpen] = useState(false);
  const [editScheduleForm, setEditScheduleForm] = useState({ id: '', title: '', client_id: '', visit_date: '', visit_end_time: '17:00', assigned_staff: [], purpose: '' });

  const loadData = useCallback(async () => {
    try {
      const [visitsRes, clientsRes, staffRes] = await Promise.all([
        visitAPI.getAll(),
        clientAPI.getAll(),
        userAPI.lookup({ role: 'staff' }).catch(() => ({ data: [] })),
      ]);

      setVisits(visitsRes.data);
      setClients(clientsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Deep-link support: open detail when ?id=... is passed
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const visitId = searchParams.get('id');
    if (visitId && visits.length > 0) {
      const v = visits.find(x => x.id === visitId);
      if (v) {
        setSelectedVisit(v);
        setIsDetailOpen(true);
      }
    }
  }, [searchParams, visits]);

  // Check leave conflicts whenever date changes
  const checkLeaveForDate = async (dateStr) => {
    if (!dateStr) { setStaffOnLeave({}); return; }
    try {
      const dateOnly = dateStr.split('T')[0];
      const res = await visitAPI.checkLeaveConflicts(dateOnly);
      setStaffOnLeave(res.data.staff_on_leave || {});
    } catch {
      setStaffOnLeave({});
    }
  };

  const handleDateChange = (value) => {
    setFormData(prev => ({ ...prev, visit_date: value }));
    checkLeaveForDate(value);
  };


  const handleSubmitVisitSummary = async () => {
    if (!summaryText.trim()) { toast.error('Summary is required'); return; }
    try {
      await visitAPI.submitSummary(selectedVisit.id, { summary: summaryText });
      toast.success('Visit summary submitted for approval');
      setIsSummaryOpen(false); setSummaryText(''); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleVisitSummaryAction = async (action) => {
    try {
      const payload = { action, remarks: summaryRemarks };
      if (action === 'approve' && summaryEditText) payload.completion_summary = summaryEditText;
      await visitAPI.approveSummary(selectedVisit.id, payload);
      toast.success(action === 'approve' ? 'Visit verified & approved — click Send to Client when ready' : 'Summary rejected');
      setIsSummaryActionOpen(false); setSummaryRemarks(''); setSummaryEditText(''); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleSendVisitSummaryToClient = async (visit) => {
    if (!window.confirm(`Send the approved visit summary to client (${visit.client_id ? getClientName(visit.client_id) : 'N/A'})?`)) return;
    try {
      const res = await visitAPI.sendSummaryToClient(visit.id);
      toast.success(res.data.message || 'Summary sent to client');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send summary to client');
    }
  };

  const handleDownloadSummary = async (visit) => {
    try {
      const res = await visitAPI.downloadSummary(visit.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Visit_Summary_${visit.title?.replace(/\s+/g, '_') || visit.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Summary downloaded');
    } catch {
      toast.error('Failed to download summary');
    }
  };


  const handleCreate = async (e) => {
    e.preventDefault();
    // Warn if any assigned staff are on leave
    const onLeaveStaff = formData.assigned_staff.filter(id => staffOnLeave[id]);
    if (onLeaveStaff.length > 0) {
      const names = onLeaveStaff.map(id => staffOnLeave[id]?.staff_name || id).join(', ');
      if (!window.confirm(`Warning: ${names} is on approved leave on this date. Continue anyway?`)) {
        return;
      }
    }

    // ============ VISIT CONFLICT CHECK (busy staff) ============
    // For both single & bulk: pull date(s), start_time, end_time and call check-conflicts
    try {
      const startDateStr = formData.visit_date.split('T')[0];
      const startTimeStr = formData.visit_date.includes('T')
        ? formData.visit_date.split('T')[1].slice(0, 5)
        : '09:00';
      const endTimeStr = formData.visit_end_time || '17:00';
      const datesToCheck = [];
      if (isDateRange && dateRangeEnd) {
        const e2 = new Date(`${dateRangeEnd}T00:00:00Z`);
        for (let d = new Date(`${startDateStr}T00:00:00Z`); d <= e2; d.setUTCDate(d.getUTCDate() + 1)) {
          datesToCheck.push(d.toISOString().split('T')[0]);
        }
      } else {
        datesToCheck.push(startDateStr);
      }
      const allConflicts = [];
      for (const d of datesToCheck) {
        const res = await visitAPI.checkVisitConflicts({
          date: d,
          start_time: startTimeStr,
          end_time: endTimeStr,
          staff_ids: formData.assigned_staff,
        });
        if (res.data?.has_conflict) {
          (res.data.conflicts || []).forEach((c) => allConflicts.push({ ...c, date: d }));
        }
      }
      if (allConflicts.length > 0) {
        setConflictDialog({ open: true, conflicts: allConflicts });
        return; // BLOCK submit until user changes time/staff
      }
    } catch (err) {
      console.error('Conflict check failed', err);
      // Fall through — don't block on a backend hiccup
    }

    try {
      if (isDateRange && dateRangeEnd) {
        // Bulk create for date range
        const startDate = formData.visit_date.split('T')[0];
        const visitTime = formData.visit_date.includes('T') ? formData.visit_date.split('T')[1] : '09:00';
        const res = await visitAPI.bulkCreate({
          client_id: formData.client_id || null,
          title: formData.title,
          start_date: startDate,
          end_date: dateRangeEnd,
          visit_time: visitTime,
          end_time: formData.visit_end_time || '17:00',
          assigned_staff: formData.assigned_staff,
          purpose: formData.purpose,
        });
        if (res.data.warnings?.length > 0) {
          res.data.warnings.forEach(w => toast.warning(w));
        }
        toast.success(`${res.data.total_created} visits scheduled successfully!`);
      } else {
        // Single visit
        const payload = {
          ...formData,
          visit_date: istInputToISO(formData.visit_date),
          end_time: formData.visit_end_time || null,
        };
        const res = await visitAPI.create(payload);
        if (res.data.leave_warnings?.length > 0) {
          res.data.leave_warnings.forEach(w => toast.warning(w));
        }
        toast.success('Visit scheduled successfully!');
      }
      setIsCreateOpen(false);
      setFormData({ client_id: '', title: '', visit_date: '', visit_end_time: '17:00', assigned_staff: [], purpose: '' });
      setStaffOnLeave({});
      setIsDateRange(false);
      setDateRangeEnd('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule visit');
    }
  };

  const openEditSchedule = (v) => {
    // Pre-fill the dialog with the visit's current schedule
    const isoDate = toISTInput(v.visit_date);
    setEditScheduleForm({
      id: v.id,
      title: v.title || '',
      client_id: v.client_id || '',
      visit_date: isoDate,
      visit_end_time: v.end_time || '17:00',
      assigned_staff: v.assigned_staff || [],
      purpose: v.purpose || '',
    });
    setIsEditScheduleOpen(true);
  };

  const handleSaveEditSchedule = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: editScheduleForm.title,
        visit_date: istInputToISO(editScheduleForm.visit_date),
        end_time: editScheduleForm.visit_end_time || null,
        assigned_staff: editScheduleForm.assigned_staff,
        purpose: editScheduleForm.purpose,
      };
      await visitAPI.update(editScheduleForm.id, payload);
      // Sync roster: remove old auto-synced rows then recreate is server side concern,
      // but for now just delete + the visit update endpoint already preserves roster.
      toast.success('Visit schedule updated');
      setIsEditScheduleOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update visit');
    }
  };

  const handleStatusChange = async (visitId, newStatus) => {
    try {
      await visitAPI.update(visitId, { status: newStatus });
      toast.success('Visit status updated!');
      loadData();
    } catch (error) {
      toast.error('Failed to update visit');
    }
  };

  const handleAddReport = async (visitId, report, findings) => {
    try {
      const updateData = { report, status: 'completed' };
      if (findings) updateData.findings = findings;
      await visitAPI.update(visitId, updateData);
      toast.success('Visit report saved!');
      setIsDetailOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  const toggleStaffAssignment = (staffId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_staff: prev.assigned_staff.includes(staffId)
        ? prev.assigned_staff.filter((id) => id !== staffId)
        : [...prev.assigned_staff, staffId],
    }));
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.organization_name || 'Unknown';
  };

  const getStaffNames = (staffIds) => {
    return staffIds
      .map((id) => staff.find((s) => s.id === id)?.name)
      .filter(Boolean)
      .join(', ') || 'Unassigned';
  };

  const visitsOnDate = visits
    .filter((v) => toISTInput(v.visit_date).slice(0, 10) === localYMD(selectedDate))
    .sort((a, b) => {
      // Latest scheduled time / latest created first
      const aTime = new Date(a.created_at || a.visit_date).getTime();
      const bTime = new Date(b.created_at || b.visit_date).getTime();
      return bTime - aTime;
    });

  const datesWithVisits = visits.map((v) => {
    const [y, m, d] = toISTInput(v.visit_date).slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="visits-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 via-blue-500 to-sky-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Visit Management</h1>
            <p className="text-indigo-100">Schedule and track consultant visits</p>
          </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) { setStaffOnLeave({}); } }}>
          <DialogTrigger asChild>
            <Button data-testid="schedule-visit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Visit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule New Visit</DialogTitle>
              <DialogDescription>Plan a consultant visit to a client</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">Client (optional)</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="visit-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.organization_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Visit Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Gap Analysis Visit"
                  required
                  data-testid="visit-title-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="visit_date">Visit Date & Time *</Label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={isDateRange} onChange={e => { setIsDateRange(e.target.checked); if (!e.target.checked) setDateRangeEnd(''); }} className="rounded border-gray-300" />
                    <span className="text-xs text-muted-foreground">Multi-day (date range)</span>
                  </label>
                </div>
                {isDateRange ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-xs text-muted-foreground">From Date & Time</span>
                        <Input
                          type="datetime-local"
                          value={formData.visit_date}
                          onChange={(e) => handleDateChange(e.target.value)}
                          required
                          data-testid="visit-start-date-input"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">To Date</span>
                        <Input
                          type="date"
                          value={dateRangeEnd}
                          onChange={(e) => setDateRangeEnd(e.target.value)}
                          min={formData.visit_date?.split('T')[0]}
                          required
                          data-testid="visit-end-date-input"
                        />
                      </div>
                    </div>
                    {formData.visit_date && dateRangeEnd && (
                      <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                        {Math.floor((new Date(dateRangeEnd) - new Date(formData.visit_date.split('T')[0])) / 86400000) + 1} daily visits will be created
                      </p>
                    )}
                  </div>
                ) : (
                  <Input
                    id="visit_date"
                    type="datetime-local"
                    value={formData.visit_date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    required
                    data-testid="visit-date-input"
                  />
                )}
              </div>

              {/* End Time */}
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.visit_end_time}
                  onChange={(e) => setFormData({ ...formData, visit_end_time: e.target.value })}
                  data-testid="visit-end-time-input"
                />
                <p className="text-xs text-muted-foreground">
                  {isDateRange ? 'Same time range will apply to all days in the range' : 'Expected visit end time'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Assign Staff</Label>
                {Object.keys(staffOnLeave).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 flex items-start gap-2" data-testid="visit-leave-banner">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-700">Some staff members have approved leave on this date. They are marked below.</p>
                  </div>
                )}
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {staff.map((s) => {
                    const leaveInfo = staffOnLeave[s.id];
                    return (
                      <div key={s.id} className={`flex items-center gap-2 rounded px-1 py-0.5 ${leaveInfo ? 'bg-red-50' : ''}`}>
                        <Checkbox
                          checked={formData.assigned_staff.includes(s.id)}
                          onCheckedChange={() => toggleStaffAssignment(s.id)}
                          data-testid={`visit-staff-${s.id}`}
                        />
                        <span className={`text-sm ${leaveInfo ? 'text-red-700' : ''}`}>{s.name}</span>
                        {leaveInfo && (
                          <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto" data-testid={`leave-badge-${s.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            On {leaveInfo.leave_type} leave ({leaveInfo.start_date} to {leaveInfo.end_date})
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose</Label>
                <Textarea
                  id="purpose"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  rows={3}
                  placeholder="Describe the visit objectives..."
                  data-testid="visit-purpose-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-visit-btn">Schedule Visit</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Calendar and List View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1" data-testid="visit-calendar">
          <CardHeader>
            <CardTitle className="text-lg">Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border"
              modifiers={{ hasVisit: datesWithVisits }}
              modifiersStyles={{ hasVisit: { backgroundColor: 'hsl(var(--primary) / 0.1)', fontWeight: 'bold' } }}
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Visits on {formatDate(selectedDate)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {visitsOnDate.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No visits scheduled for this date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {visitsOnDate.map((visit) => (
                  <div
                    key={visit.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => { setSelectedVisit(visit); setIsDetailOpen(true); }}
                    data-testid={`visit-card-${visit.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{visit.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{getClientName(visit.client_id)}</p>
                      </div>
                      <Badge className={getStatusColor(visit.status)}>{visit.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(visit.visit_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {getStaffNames(visit.assigned_staff)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Visits List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Visits</CardTitle>
        </CardHeader>
        <CardContent>
          {visits.length === 0 ? (
            <p className="text-center py-4 text-muted-foreground">No visits found</p>
          ) : (
            <div className="space-y-3">
              {visits
                .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
                .slice(0, showAllVisits ? undefined : 20)
                .map((visit) => (
                  <div key={visit.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <CalendarIcon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">{visit.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {visit.client_id ? getClientName(visit.client_id) : 'Internal'} &middot; {formatDateTime(visit.visit_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(visit.status)}>{visit.status}</Badge>
                      {visit.summary_status && (
                        <Badge className={visit.summary_status === 'approved' ? 'bg-green-100 text-green-700' : visit.summary_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                          Summary: {visit.summary_status?.replace('_', ' ')}
                        </Badge>
                      )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`visit-actions-${visit.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setSelectedVisit(visit); setIsDetailOpen(true); }}>View Details</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {visit.status === 'scheduled' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(visit.id, 'completed')}>Mark Completed</DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={async () => {
                            try {
                              const res = await visitAPI.downloadIcs(visit.id);
                              const blob = new Blob([res.data], { type: 'text/calendar' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `visit-${visit.id.slice(0,8)}.ics`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Calendar file downloaded — open it to add to Google/Outlook');
                            } catch (e) {
                              toast.error('Failed to download calendar file');
                            }
                          }}
                          data-testid={`visit-ics-${visit.id}`}
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" /> Add to Calendar (.ics)
                        </DropdownMenuItem>
                        {isAdmin() && visit.status === 'scheduled' && (
                          <DropdownMenuItem
                            onClick={() => openEditSchedule(visit)}
                            className="text-blue-600"
                            data-testid={`visit-edit-schedule-${visit.id}`}
                          >
                            <Edit2 className="w-4 h-4 mr-2" /> Edit Schedule
                          </DropdownMenuItem>
                        )}
                        {visit.status === 'completed' && !visit.completion_summary && !isAdmin() && (
                          <DropdownMenuItem onClick={() => { setSelectedVisit(visit); setSummaryText(''); setIsSummaryOpen(true); }}>
                            <ClipboardCheck className="w-4 h-4 mr-2" /> Write Summary
                          </DropdownMenuItem>
                        )}
                        {(visit.status === 'completed' || visit.completion_summary || visit.summary_status) && (
                          <DropdownMenuItem onClick={() => handleDownloadSummary(visit)} data-testid={`visit-download-summary-${visit.id}`}>
                            <Download className="w-4 h-4 mr-2" /> Download Summary
                          </DropdownMenuItem>
                        )}
                        {isAdmin() && (visit.summary_status === 'approved' || visit.status === 'completed') && !visit.summary_sent_to_client && visit.client_id && (
                          <DropdownMenuItem onClick={() => handleSendVisitSummaryToClient(visit)} className="text-orange-600" data-testid={`visit-send-client-${visit.id}`}>
                            <Mail className="w-4 h-4 mr-2" /> Send Summary to Client
                          </DropdownMenuItem>
                        )}
                        {visit.summary_sent_to_client && (
                          <DropdownMenuItem disabled className="text-emerald-600">
                            <MailCheck className="w-4 h-4 mr-2" /> Summary Sent to Client
                          </DropdownMenuItem>
                        )}
                        {isAdmin() && visit.summary_status === 'pending_approval' && (
                          <DropdownMenuItem onClick={() => { setSelectedVisit(visit); setSummaryRemarks(''); setSummaryEditText(visit.completion_summary || ''); setIsSummaryActionOpen(true); }} className="text-purple-600">
                            <ClipboardCheck className="w-4 h-4 mr-2" /> Review &amp; Approve Summary
                          </DropdownMenuItem>
                        )}
                        {visit.status === 'scheduled' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(visit.id, 'cancelled')} className="text-red-600">Cancel Visit</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  </div>
                ))}
              {visits.length > 20 && (
                <Button variant="outline" className="w-full" onClick={() => setShowAllVisits((v) => !v)} data-testid="toggle-all-visits-btn">
                  {showAllVisits ? 'Show latest 20' : `Show all ${visits.length} visits`}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visit Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedVisit?.title}</DialogTitle>
            <DialogDescription>Visit details and report</DialogDescription>
          </DialogHeader>
          {selectedVisit && (
            <VisitDetailContent
              visit={selectedVisit}
              clientName={getClientName(selectedVisit.client_id)}
              staffNames={getStaffNames(selectedVisit.assigned_staff)}
              onAddReport={(report, findings) => handleAddReport(selectedVisit.id, report, findings)}
              isAdmin={isAdmin}
              onWriteSummary={() => { setIsDetailOpen(false); setSummaryText(''); setIsSummaryOpen(true); }}
              onReviewSummary={() => { setIsDetailOpen(false); setSummaryRemarks(''); setSummaryEditText(selectedVisit?.completion_summary || ''); setIsSummaryActionOpen(true); }}
              onAdminEdit={() => { setIsDetailOpen(false); setAdminEditForm({ report: selectedVisit?.report || '', findings: selectedVisit?.findings || '', completion_summary: selectedVisit?.completion_summary || '' }); setIsAdminEditOpen(true); }}
              onDownloadSummary={() => handleDownloadSummary(selectedVisit)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Write Summary Dialog */}
      <Dialog open={isSummaryOpen} onOpenChange={setIsSummaryOpen}>
        <DialogContent className="max-w-lg" data-testid="visit-summary-dialog">
          <DialogHeader>
            <DialogTitle>Write Visit Summary</DialogTitle>
            <DialogDescription>Write a summary for: {selectedVisit?.title}</DialogDescription>
          </DialogHeader>
          <Textarea value={summaryText} onChange={e => setSummaryText(e.target.value)} placeholder="Describe what was accomplished, key findings, next steps..." rows={5} data-testid="visit-summary-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSummaryOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitVisitSummary} data-testid="submit-visit-summary-btn">Submit for Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Review Dialog */}
      <Dialog open={isSummaryActionOpen} onOpenChange={setIsSummaryActionOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="visit-summary-action-dialog">
          <DialogHeader>
            <DialogTitle>Verify &amp; Approve Visit Summary</DialogTitle>
            <DialogDescription>{selectedVisit?.title} — review, edit, and approve in one step</DialogDescription>
          </DialogHeader>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-xs">
            <p className="font-medium text-blue-800 mb-1">Staff's original summary:</p>
            <p className="whitespace-pre-wrap break-words max-h-[20vh] overflow-y-auto">{selectedVisit?.completion_summary || 'No summary submitted'}</p>
          </div>
          <div className="space-y-2">
            <Label>Final Summary for Client (editable)</Label>
            <Textarea
              value={summaryEditText}
              onChange={e => setSummaryEditText(e.target.value)}
              rows={6}
              placeholder="Edit/finalize the summary that will be sent to the client..."
              data-testid="visit-summary-edit"
            />
            <p className="text-xs text-muted-foreground">After approval, click the orange "Send Summary to Client" button to email this content (with attachments).</p>
          </div>
          <div className="space-y-2">
            <Label>Remarks (optional, required for rejection)</Label>
            <Textarea value={summaryRemarks} onChange={e => setSummaryRemarks(e.target.value)} rows={2} placeholder="Reason for rejection..." />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsSummaryActionOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleVisitSummaryAction('reject')}>Reject</Button>
            <Button onClick={() => handleVisitSummaryAction('approve')} data-testid="approve-visit-summary-btn">Verify &amp; Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Visit Dialog */}
      <Dialog open={isAdminEditOpen} onOpenChange={setIsAdminEditOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-visit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Visit Report</DialogTitle>
            <DialogDescription>Admin edit — directly modify the report, findings, and summary for: {selectedVisit?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Report</Label>
              <Textarea
                value={adminEditForm.report}
                onChange={e => setAdminEditForm(f => ({ ...f, report: e.target.value }))}
                rows={3}
                placeholder="Visit report..."
                data-testid="admin-edit-report"
              />
            </div>
            <div className="space-y-2">
              <Label>Key Findings</Label>
              <Textarea
                value={adminEditForm.findings}
                onChange={e => setAdminEditForm(f => ({ ...f, findings: e.target.value }))}
                rows={3}
                placeholder="Key findings..."
                data-testid="admin-edit-findings"
              />
            </div>
            <div className="space-y-2">
              <Label>Final Summary (for client)</Label>
              <Textarea
                value={adminEditForm.completion_summary}
                onChange={e => setAdminEditForm(f => ({ ...f, completion_summary: e.target.value }))}
                rows={5}
                placeholder="Final summary that will be sent to the client..."
                data-testid="admin-edit-completion-summary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdminEditOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                await visitAPI.adminEdit(selectedVisit.id, adminEditForm);
                toast.success('Visit updated');
                setIsAdminEditOpen(false);
                loadData();
              } catch { toast.error('Failed to update'); }
            }} data-testid="save-admin-edit-visit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Schedule Dialog (planned visits) */}
      <Dialog open={isEditScheduleOpen} onOpenChange={setIsEditScheduleOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-visit-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Visit Schedule
            </DialogTitle>
            <DialogDescription>Change date, time, staff or purpose of an already-scheduled visit.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEditSchedule} className="space-y-3">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={editScheduleForm.title}
                onChange={e => setEditScheduleForm(f => ({ ...f, title: e.target.value }))}
                required
                data-testid="edit-visit-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date &amp; Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={editScheduleForm.visit_date}
                  onChange={e => setEditScheduleForm(f => ({ ...f, visit_date: e.target.value }))}
                  required
                  data-testid="edit-visit-datetime"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={editScheduleForm.visit_end_time}
                  onChange={e => setEditScheduleForm(f => ({ ...f, visit_end_time: e.target.value }))}
                  data-testid="edit-visit-endtime"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assigned Staff</Label>
              <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                {staff.map(s => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                    <input
                      type="checkbox"
                      checked={editScheduleForm.assigned_staff.includes(s.id)}
                      onChange={e => {
                        const ids = e.target.checked
                          ? [...editScheduleForm.assigned_staff, s.id]
                          : editScheduleForm.assigned_staff.filter(x => x !== s.id);
                        setEditScheduleForm(f => ({ ...f, assigned_staff: ids }));
                      }}
                      data-testid={`edit-visit-staff-${s.id}`}
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose / Notes</Label>
              <Textarea
                rows={2}
                value={editScheduleForm.purpose}
                onChange={e => setEditScheduleForm(f => ({ ...f, purpose: e.target.value }))}
                data-testid="edit-visit-purpose"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditScheduleOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="edit-visit-save-btn">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visit Schedule Conflict Dialog (red) */}
      <Dialog open={conflictDialog.open} onOpenChange={(o) => setConflictDialog({ open: o, conflicts: o ? conflictDialog.conflicts : [] })}>
        <DialogContent className="sm:max-w-lg border-2 border-red-500" data-testid="visit-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Schedule Conflict — Staff Already Booked
            </DialogTitle>
            <DialogDescription className="text-red-600">
              The following staff already have a visit/roster entry in the chosen time window. Please pick a different time or assign another staff member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {conflictDialog.conflicts.map((c, idx) => (
              <div key={idx} className="rounded-lg border-2 border-red-300 bg-red-50 p-3" data-testid={`conflict-row-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-red-900">{c.staff_name}</p>
                  <Badge className="bg-red-600 text-white">{c.date}</Badge>
                </div>
                <ul className="text-sm text-red-800 space-y-1 mt-1">
                  {(c.items || []).map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>{it.start_time}{it.end_time ? `–${it.end_time}` : ''}</strong>
                        {' · '}{it.title}
                        {it.client_name ? <> · <em>{it.client_name}</em></> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-50"
              onClick={() => setConflictDialog({ open: false, conflicts: [] })}
              data-testid="conflict-dialog-close-btn"
            >
              Change Time / Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VisitDetailContent = ({ visit, clientName, staffNames, onAddReport, isAdmin, onWriteSummary, onReviewSummary, onDownloadSummary, onAdminEdit }) => {
  const [report, setReport] = useState(visit.report || '');
  const [findings, setFindings] = useState(visit.findings || '');

  const handleSubmitReport = () => {
    if (!report.trim()) return;
    onAddReport(report, findings);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
        <div>
          <p className="text-sm text-muted-foreground">Client</p>
          <p className="font-medium">{clientName}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Status</p>
          <Badge className={getStatusColor(visit.status)}>{visit.status}</Badge>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Date & Time</p>
          <p className="font-medium">{formatDateTime(visit.visit_date)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Assigned Staff</p>
          <p className="font-medium">{staffNames}</p>
        </div>
        {visit.purpose && (
          <div className="col-span-2">
            <p className="text-sm text-muted-foreground">Purpose</p>
            <p className="font-medium">{visit.purpose}</p>
          </div>
        )}
      </div>

      {visit.status === 'scheduled' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="report">Visit Report *</Label>
            <Textarea id="report" value={report} onChange={(e) => setReport(e.target.value)} rows={4} placeholder="Summarize the visit outcomes..." data-testid="visit-report-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="findings">Key Findings</Label>
            <Textarea id="findings" value={findings} onChange={(e) => setFindings(e.target.value)} rows={3} placeholder="List key findings..." data-testid="visit-findings-input" />
          </div>
          <Button onClick={handleSubmitReport} className="w-full" disabled={!report.trim()} data-testid="submit-report-btn">
            <FileText className="w-4 h-4 mr-2" /> Submit Report & Complete Visit
          </Button>
        </div>
      )}

      {visit.status === 'completed' && visit.report && (
        <div className="space-y-3">
          <div>
            <h4 className="font-medium mb-2">Visit Report</h4>
            <div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm whitespace-pre-wrap">{visit.report}</p></div>
          </div>
          {visit.findings && (
            <div>
              <h4 className="font-medium mb-2">Key Findings</h4>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200"><p className="text-sm whitespace-pre-wrap">{visit.findings}</p></div>
            </div>
          )}
        </div>
      )}

      {/* Summary Section */}
      {visit.completion_summary && (
        <div className={`p-3 rounded-lg border max-h-[40vh] overflow-y-auto ${visit.summary_status === 'approved' ? 'bg-green-50 border-green-200' : visit.summary_status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <p className="font-medium text-xs mb-1">Completion Summary ({visit.summary_status?.replace('_', ' ')})</p>
          <p className="text-sm whitespace-pre-wrap break-words">{visit.completion_summary}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-2 flex-wrap">
        {visit.status === 'completed' && !visit.completion_summary && (!isAdmin || !isAdmin()) && (
          <Button size="sm" variant="outline" onClick={onWriteSummary}><ClipboardCheck className="w-4 h-4 mr-1" /> Write Summary</Button>
        )}
        {isAdmin && isAdmin() && (visit.summary_status === 'pending_approval' || (visit.status === 'completed' && visit.summary_status !== 'approved')) && (
          <Button size="sm" onClick={onReviewSummary} className="bg-purple-600 hover:bg-purple-700"><ClipboardCheck className="w-4 h-4 mr-1" /> Verify &amp; Approve</Button>
        )}
        {isAdmin && isAdmin() && (
          <Button size="sm" variant="outline" onClick={onAdminEdit} data-testid="admin-edit-visit-btn"><Edit2 className="w-4 h-4 mr-1" /> Edit Report</Button>
        )}
        {visit.completion_summary && visit.summary_status === 'approved' && (
          <Button size="sm" variant="outline" onClick={onDownloadSummary}><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
        )}
      </div>
    </div>
  );
};

export default VisitsPage;

